/**
 * Maverick Capability Registry — single source of truth for "what does
 * Maverick actually do?"
 *
 * One helper. Two consumers:
 *   • The user-facing "How Maverick works" drawer (legibility for beta users).
 *   • The DEV-only "Test Maverick" checklist (reproduction guidance for QA).
 *
 * Both read from this file so the explainer can never drift from the engine.
 * If a generator/failure-mode is added or removed, update THIS file — the
 * UI follows automatically.
 *
 * Two engines power Maverick today; both are represented:
 *   1. preMortemEngine  → drives the full-screen Loop Closure UI (failure modes)
 *   2. assistEngine     → drives the inline recommendation strip (rec types)
 */

import type { PredictedFailureMode } from "./preMortemEngine";
import type { MaverickRecommendationType } from "./assistTypes";

export type CloseMode =
  /** Operator finishes the work without leaving Maverick (modal or inline). */
  | "inline"
  /** Operator opens a modal hosted by Maverick that closes the loop on submit. */
  | "modal"
  /** Soft hand-off to another module; loop closes only on explicit return. */
  | "route";

export type CapabilityCategory =
  | "materials"
  | "permits"
  | "communication"
  | "financial"
  | "follow_up"
  | "crew"
  | "issues"
  | "convergence"
  | "queue_health";

export interface MaverickCapability {
  /** Stable engine key — matches PredictedFailureMode or MaverickRecommendationType. */
  key: string;
  /** Which engine emits this. */
  engine: "preMortem" | "assist";
  /** Plain-English name. */
  name: string;
  /** One-line description for the explainer drawer. */
  description: string;
  category: CapabilityCategory;
  /** How the operator finishes this kind of work. */
  closeMode: CloseMode;
  /**
   * Whether this capability surfaces in the PRIMARY One Thing slot or only
   * appears in "Show all" / observational footers.
   */
  primarySurface: boolean;
  /** What signal in the data triggers this — for the test checklist. */
  trigger: string;
}

/* ─── preMortemEngine capabilities (full-screen Loop Closure UI) ─── */

const PRE_MORTEM_CAPABILITIES: ReadonlyArray<MaverickCapability> = [
  {
    key: "material_will_miss_install" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Material slip risk",
    description: "Detects jobs where materials aren't staged with install in the next 5 days.",
    category: "materials",
    closeMode: "modal",
    primarySurface: true,
    trigger: "Install scheduled within 5 days AND material readiness is not Loaded/Staged.",
  },
  {
    key: "permit_will_block_schedule" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Permit stall",
    description: "Detects permits stuck in revision or under review for more than 5 days.",
    category: "permits",
    closeMode: "route",
    primarySurface: true,
    trigger: "Permit status is Revision Needed, OR Under Review for 5+ days, with install in horizon.",
  },
  {
    key: "customer_about_to_escalate" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Customer escalation risk",
    description: "Detects unanswered inbound messages older than 24h on active jobs.",
    category: "communication",
    closeMode: "modal",
    primarySurface: true,
    trigger: "Inbound message with no reply for 24+ hours.",
  },
  {
    key: "install_will_callback" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Service callback risk",
    description: "Detects high Service Call Prevention risk before install completes.",
    category: "issues",
    closeMode: "route",
    primarySurface: true,
    trigger: "Service Call Risk score crosses elevated threshold during install window.",
  },
  {
    key: "financial_will_block_closeout" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Financial closeout block",
    description: "Detects balance/funding gaps that will block job completion.",
    category: "financial",
    closeMode: "route",
    primarySurface: true,
    trigger: "Financial integrity check fails on a near-completion job.",
  },
  {
    key: "followup_dropping_ball" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Overdue follow-ups",
    description: "Detects multiple overdue follow-ups on the same job.",
    category: "follow_up",
    closeMode: "inline",
    primarySurface: true,
    trigger: "2+ overdue follow-up tasks attached to one job.",
  },
  {
    key: "unresolved_critical_issue" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Unresolved critical issue",
    description: "Detects open Urgent-severity issues lingering on active jobs.",
    category: "issues",
    closeMode: "modal",
    primarySurface: true,
    trigger: "Issue with severity Urgent and status Open/In Progress.",
  },
  {
    key: "build_review_stalled" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Build Review stall",
    description:
      "Detects jobs sitting in Build Review long enough that they're not ready for ordering once rescission clears.",
    category: "convergence",
    closeMode: "route",
    primarySurface: true,
    trigger:
      "Job in 'Review and Validation' stage with no approval event for 2+ days (anchored on latest BuildReviewEvent or job.dateCreated).",
  },
  {
    key: "crew_unreliable_for_install" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Crew reliability risk",
    description: "Detects assigned crews with reliability concerns before install.",
    category: "crew",
    closeMode: "route",
    primarySurface: true,
    trigger: "Crew reliability flag fires on a job with install in horizon.",
  },
  {
    key: "payment_not_collected_post_completion" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Post-completion cash exposure",
    description: "Detects completed jobs where deposits/balance haven't cleared the morning after.",
    category: "financial",
    closeMode: "route",
    primarySurface: true,
    trigger: "Job marked Completed with material payment exception detected.",
  },
  {
    key: "funding_not_confirmed_post_completion" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Post-completion funding exposure",
    description: "Detects completed financed jobs where funding hasn't been confirmed.",
    category: "financial",
    closeMode: "route",
    primarySurface: true,
    trigger: "Financed job marked Completed without funding confirmation.",
  },
  {
    key: "readiness_degrading" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Readiness degrading (trend)",
    description: "Trend signal — readiness score sliding without a specific blocker yet.",
    category: "convergence",
    closeMode: "route",
    primarySurface: false,
    trigger: "Readiness score regression without a single dominant cause. Visible in 'Show all'.",
  },
  {
    key: "duplicate_attention_chaos" satisfies PredictedFailureMode,
    engine: "preMortem",
    name: "Duplicate attention (trend)",
    description: "Trend signal — same job pulling attention from multiple lanes.",
    category: "convergence",
    closeMode: "route",
    primarySurface: false,
    trigger: "Same job has open signals across handoffs + queue + AI lanes. Visible in 'Show all'.",
  },
];

/* ─── assistEngine capabilities (inline recommendation strip) ─── */

const ASSIST_CAPABILITIES: ReadonlyArray<MaverickCapability> = [
  {
    key: "stalled_queue_item" satisfies MaverickRecommendationType,
    engine: "assist",
    name: "Stalled queue item",
    description: "Recommends a push when a queue item has been open 48+ hours.",
    category: "queue_health",
    closeMode: "route",
    primarySurface: true,
    trigger: "ActionQueueItem open or in_progress for 48+ hours.",
  },
  {
    key: "unowned_work" satisfies MaverickRecommendationType,
    engine: "assist",
    name: "Unowned work",
    description: "Recommends assigning open queue items that have no owner.",
    category: "queue_health",
    closeMode: "route",
    primarySurface: true,
    trigger: "Open queue item without assignedToRoleKey or assignedToUserId.",
  },
  {
    key: "handoff_without_followthrough" satisfies MaverickRecommendationType,
    engine: "assist",
    name: "Handoff without follow-through",
    description: "Flags completed handoffs that didn't spawn downstream work after 6h.",
    category: "follow_up",
    closeMode: "route",
    primarySurface: true,
    trigger: "Handoff completed 6+ hours ago with no linked queue item created.",
  },
  {
    key: "linked_work_mismatch" satisfies MaverickRecommendationType,
    engine: "assist",
    name: "Linked work mismatch",
    description: "Flags handoffs whose linked queue item is still open 24h later.",
    category: "follow_up",
    closeMode: "route",
    primarySurface: true,
    trigger: "Handoff completed but its linkedQueueItemId is still open after 24h.",
  },
  {
    key: "duplicate_attention_on_same_job" satisfies MaverickRecommendationType,
    engine: "assist",
    name: "Duplicate attention on same job",
    description: "Flags jobs receiving attention across 2+ lanes simultaneously.",
    category: "convergence",
    closeMode: "route",
    primarySurface: true,
    trigger: "Same jobId open in 2+ lanes (handoffs, queue, AI signals) with 3+ total items.",
  },
];

/**
 * Single source of truth. Returns every capability Maverick currently
 * exposes, across both engines. Order is stable: pre-mortem first (the
 * loud surface), then assist (the quiet strip).
 */
export function getMaverickCapabilities(): ReadonlyArray<MaverickCapability> {
  return [...PRE_MORTEM_CAPABILITIES, ...ASSIST_CAPABILITIES];
}

/** Group capabilities by category — used by the explainer drawer. */
export function getCapabilitiesByCategory(): Record<CapabilityCategory, MaverickCapability[]> {
  const out = {} as Record<CapabilityCategory, MaverickCapability[]>;
  for (const cap of getMaverickCapabilities()) {
    (out[cap.category] ||= []).push(cap);
  }
  return out;
}

export const CATEGORY_LABELS: Record<CapabilityCategory, string> = {
  materials: "Materials",
  permits: "Permits",
  communication: "Communication",
  financial: "Financial",
  follow_up: "Follow-ups",
  crew: "Crew",
  issues: "Issues",
  convergence: "Convergence",
  queue_health: "Queue health",
};

export const CLOSE_MODE_LABELS: Record<CloseMode, string> = {
  inline: "Closes inline",
  modal: "Closes via modal",
  route: "Hand-off to module",
};
